import Decimal from "decimal.js";
import { ChartLists, GlobalStore } from "store/global/GlobalStore";
import { TokenList } from "store/global/getAllTokenLists";
import { Balances } from "store/global/getBalances";
import { Amount } from "types/amount";
import { Chain } from "wagmi";
import { getWrappedTokenAddress } from "./chainUtils";
import { getPriceForTokenByTokenList } from "./tokenListUtils";
import { isNativeToken } from "./wrapUtils";
import { CounterParty } from "./tradeUtils";
import percentRound from "percent-round";
import { Hash } from "viem";
import { NATIVE_TOKEN_ADDRESS } from "config/consts";
import { isAddressEqual } from "./tokenUtils";
import { getChartData } from "../store/global/getChartData";
import { TokenListMetadata } from "../hooks/queries/tokens-queries";
import { compact, filter } from "lodash-es";

export const sortTokens = (
  list: Balances,
  chain: Chain,
  tokenList?: TokenList,
  tokenListMetadata?: TokenListMetadata
): Balances => {
  return [...list].sort((a, b) => {
    const aPrice = getPriceForTokenByTokenList(a.tokenAddress, undefined, tokenListMetadata);
    const bPrice = getPriceForTokenByTokenList(b.tokenAddress, undefined, tokenListMetadata);
    const aAmount = aPrice.mul(a.balance);
    const bAmount = bPrice.mul(b.balance);
    if (bAmount.value.gt(0) || aAmount.value.gt(0)) {
      const result = bAmount.value.sub(aAmount.value).gt(new Decimal(0)) ? 1 : -1;
      return result;
    } else {
      if (
        (a.tokenAddress === NATIVE_TOKEN_ADDRESS ||
          a.tokenAddress === getWrappedTokenAddress(chain)) &&
        b.tokenAddress !== NATIVE_TOKEN_ADDRESS &&
        b.tokenAddress !== getWrappedTokenAddress(chain)
      ) {
        return -1;
      }

      if (
        (b.tokenAddress === NATIVE_TOKEN_ADDRESS ||
          b.tokenAddress === getWrappedTokenAddress(chain)) &&
        a.tokenAddress !== NATIVE_TOKEN_ADDRESS &&
        a.tokenAddress !== getWrappedTokenAddress(chain)
      ) {
        return 1;
      }

      const marketCapA = tokenListMetadata?.[a.tokenAddress]?.m ?? 0;
      const marketCapB = tokenListMetadata?.[b.tokenAddress]?.m ?? 0;
      return marketCapB - marketCapA;
    }
  });
};

// NOTE: "Natives" stand for either wrapped or native tokens
// Reasons below describe the current state that prevents addition or changing of tokens
export enum TokenRowDisabledReason {
  OppositePartyHasNativesAndIsNon121Trade = "OppositePartyHasNativesAndIsNon121Trade",
  SelectedTokensAlreadyIncludesNatives = "SelectedTokensAlreadyIncludesNatives",
  IsWrapTrade = "IsWrapTrade",
  CantHaveNativeAndWrappedOnOppositeSidesInManyTrade = "CantHaveNativeAndWrappedOnOppositeSidesInManyTrade",
  ChangeToWrappedNotAllowedDueToNativeInCurrentParty = "ChangeToWrappedNotAllowedDueToNativeInCurrentParty",
  ChangeToNativeNotAllowedDueToWrappedInCurrentParty = "ChangeToNativeNotAllowedDueToWrappedInCurrentParty",
}

export const getTokenRowDisabledReason = (
  symbol: Hash,
  network: Chain,
  counterParty: CounterParty,
  takerTokensArray: Array<Hash>,
  makerTokensArray: Array<Hash>,
  tokenList: TokenList | undefined,
  isChangingToken: Hash | undefined
): TokenRowDisabledReason | null => {
  const wrappedTokenAddress = getWrappedTokenAddress(network);
  const nativeSymbolForChain = NATIVE_TOKEN_ADDRESS;
  const currentSymbolIsNative = symbol === nativeSymbolForChain;
  const currentSymbolIsWrapped = symbol === wrappedTokenAddress;
  const currentPartyTokens = counterParty === "maker" ? makerTokensArray : takerTokensArray;
  const oppositePartyTokens = counterParty === "maker" ? takerTokensArray : makerTokensArray;
  const nativeInOppositeParty = oppositePartyTokens.includes(nativeSymbolForChain);
  const wrappedInOppositeParty = oppositePartyTokens.includes(wrappedTokenAddress);
  const wrappedInCurrentParty = currentPartyTokens.includes(wrappedTokenAddress);
  const nativeInCurrentParty = currentPartyTokens.includes(nativeSymbolForChain);
  const willBe121 = oppositePartyTokens.length === 1 && currentPartyTokens.length === 0; // is121 on next token selected for current party

  if (isChangingToken) {
    if (currentPartyTokens.length > 1 && nativeInOppositeParty && currentSymbolIsWrapped) {
      return TokenRowDisabledReason.CantHaveNativeAndWrappedOnOppositeSidesInManyTrade;
    }

    if (currentPartyTokens.length > 1 && wrappedInOppositeParty && currentSymbolIsNative) {
      return TokenRowDisabledReason.CantHaveNativeAndWrappedOnOppositeSidesInManyTrade;
    }

    if (
      isChangingToken !== nativeSymbolForChain &&
      currentPartyTokens.length > 1 &&
      nativeInCurrentParty &&
      currentSymbolIsWrapped
    ) {
      return TokenRowDisabledReason.ChangeToWrappedNotAllowedDueToNativeInCurrentParty;
    }
    if (
      isChangingToken !== wrappedTokenAddress &&
      currentPartyTokens.length > 1 &&
      wrappedInCurrentParty &&
      currentSymbolIsNative
    ) {
      return TokenRowDisabledReason.ChangeToNativeNotAllowedDueToWrappedInCurrentParty;
    }
  } else {
    if (
      (currentSymbolIsNative && wrappedInOppositeParty && !willBe121) ||
      (currentSymbolIsWrapped && nativeInOppositeParty && !willBe121)
    ) {
      return TokenRowDisabledReason.OppositePartyHasNativesAndIsNon121Trade;
    }
  }

  const isNativeAndWrappedSelectedInSameParty =
    currentPartyTokens.includes(wrappedTokenAddress) && currentSymbolIsNative;
  const isWrappedAndNativeSelectedInSameParty =
    currentPartyTokens.includes(nativeSymbolForChain) && currentSymbolIsWrapped;
  if (
    (isNativeAndWrappedSelectedInSameParty && !isChangingToken) ||
    (isWrappedAndNativeSelectedInSameParty && !isChangingToken)
  ) {
    return TokenRowDisabledReason.SelectedTokensAlreadyIncludesNatives;
  }

  const isWrappingFlow =
    (currentPartyTokens.length === 1 &&
      currentPartyTokens[0] === nativeSymbolForChain &&
      oppositePartyTokens.length === 1 &&
      oppositePartyTokens[0] === wrappedTokenAddress) ||
    (currentPartyTokens.length === 1 &&
      currentPartyTokens[0] === wrappedTokenAddress &&
      oppositePartyTokens.length === 1 &&
      oppositePartyTokens[0] === nativeSymbolForChain) ||
    (symbol !== nativeSymbolForChain &&
      currentPartyTokens.length === 1 &&
      currentPartyTokens[0] === nativeSymbolForChain &&
      oppositePartyTokens.length === 1 &&
      oppositePartyTokens[0] === wrappedTokenAddress) ||
    (symbol !== wrappedTokenAddress &&
      currentPartyTokens.length === 1 &&
      currentPartyTokens[0] === wrappedTokenAddress &&
      oppositePartyTokens.length === 1 &&
      oppositePartyTokens[0] === nativeSymbolForChain);

  if (isWrappingFlow && !isChangingToken) {
    return TokenRowDisabledReason.IsWrapTrade;
  }

  return null;
};

export const getIsWrapOrUnwrap = (
  takerTokens: Array<Hash>,
  makerTokens: Array<Hash>,
  chain: Chain
) => {
  if (takerTokens.length !== 1 || makerTokens.length !== 1) {
    return false;
  }

  if (
    (isNativeToken(takerTokens[0], chain) && makerTokens[0] === getWrappedTokenAddress(chain)) ||
    (isNativeToken(makerTokens[0], chain) && takerTokens[0] === getWrappedTokenAddress(chain))
  ) {
    return true;
  }
  return false;
};

export const addTokenToStore = (counterParty: CounterParty, tokenAddress: Hash) => {
  GlobalStore.update(s => {
    s.isRequestingQuote = false;
    s.quote = undefined;
  });

  if (counterParty === "taker") {
    GlobalStore.update(s => {
      s.takerTokens = [...s.takerTokens, { tokenAddress: tokenAddress, amount: Amount.ZERO }];
    });
  } else if (counterParty === "maker") {
    GlobalStore.update(s => {
      s.makerTokens = [...s.makerTokens.map(entry => entry.tokenAddress), tokenAddress].map(
        (tokenAddress, _, arr) => {
          return {
            tokenAddress,
            amount: Amount.ZERO,
            pct: 100 / arr.length,
            isLocked: false,
          };
        }
      );
    });
  }
};

export const removeTokenFromStore = (counterParty: CounterParty, tokenAddress: Hash) => {
  if (counterParty === "taker") {
    GlobalStore.update(s => {
      s.takerTokens = s.takerTokens.filter(entry => entry.tokenAddress !== tokenAddress);
    });
  } else if (counterParty === "maker") {
    GlobalStore.update(s => {
      s.makerTokens = s.makerTokens
        .filter(entry => entry.tokenAddress !== tokenAddress)
        .map((entry, _, arr) => {
          return {
            tokenAddress: entry.tokenAddress,
            amount: Amount.ZERO,
            pct: 100 / arr.length,
            isLocked: false,
          };
        });
    });
  }
};

export const removeTokenByTokenAddress = (tokenAddress: Hash) => {
  GlobalStore.update(s => {
    s.takerTokens = s.takerTokens.filter(entry => entry.tokenAddress !== tokenAddress);
    s.makerTokens = s.makerTokens
      .filter(entry => entry.tokenAddress !== tokenAddress)
      .map((entry, _, arr) => {
        return {
          tokenAddress: entry.tokenAddress,
          amount: Amount.ZERO,
          pct: 100 / arr.length,
          isLocked: false,
        };
      });
  });
};

export const changeTokenOnStore = (
  counterParty: CounterParty,
  tokenAddress: Hash,
  newTokenAddress: Hash
) => {
  if (counterParty === "taker") {
    GlobalStore.update(s => {
      const newTakerTokens = [
        ...s.takerTokens.map(entry =>
          isAddressEqual(entry.tokenAddress, tokenAddress)
            ? { tokenAddress: newTokenAddress, amount: Amount.ZERO }
            : entry
        ),
      ];
      s.takerTokens = newTakerTokens;
    });
  } else if (counterParty === "maker") {
    GlobalStore.update(s => {
      const newMakerTokens = [
        ...s.makerTokens.map((entry, index) => {
          if (isAddressEqual(entry.tokenAddress, tokenAddress)) {
            const makerArray = Object.keys(s.makerTokens);
            const newPercentages: Array<number> = new Array(makerArray.length).fill(
              100 / makerArray.length
            );
            const roundedPercentages = percentRound(newPercentages);
            return {
              tokenAddress: newTokenAddress,
              amount: Amount.ZERO,
              pct: roundedPercentages[index],
              isLocked: false,
            };
          }
          return entry;
        }),
      ];
      s.makerTokens = newMakerTokens;
    });
  }
};

export const formatMetadataValues = (value: number | null | undefined) => {
  if (value == null || value === 0) {
    return "-";
  }
  try {
    return new Amount(value.toString()).toShortFormat();
  } catch (e) {
    return "-";
  }
};

export const handleGetChartData = (
  tokenAddress: Hash,
  chain: Chain,
  chartLists: ChartLists | undefined
) => {
  if (chartLists?.[tokenAddress] === undefined) {
    void getChartData.run({ tokenAddress: tokenAddress, chain: chain });
  }
  return;
};

export const getUnselectedTokens = (
  tokenList: TokenList | undefined,
  makerTokens: Array<Hash>,
  takerTokens: Array<Hash>
) => {
  if (!tokenList) return [];
  return compact(
    filter(tokenList, entry => {
      if (entry == null) return false;
      const inMakerTokens = new Set(makerTokens).has(entry.chainInfo.contractAddress as Hash);
      const inTakerTokens = new Set(takerTokens).has(entry.chainInfo.contractAddress as Hash);
      return !inMakerTokens && !inTakerTokens;
    })
  ).map(entry => entry?.chainInfo.contractAddress) as Array<Hash>;
};
